
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import otgLogo from '../static/otgLogo.png';
import CallAnalysisBreakdown from '../callAnalysisBreakdown/CallAnalysisBreakdown';
import LostMoneyBreakdown from '../lostMoneyBreakdown/LostMoneyBreakdown';
import MarketingPerformance from '../marketingPerformance/MarketingPerformance';
import SalesProductivity from '../salesProductivity/SalesProductivity';


import './ReportQuery.css';
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { initializeApp } from 'firebase/app';

import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { DateRangePicker } from 'react-date-range';
import CallAnalysisBreakdownWithUnderlyingData from '../callAnalysisBreakdownWithUnderlyingData/CallAnalysisBreakdownWithUnderlyingData';
import SSNPullModal from '../ssnPullModal/SSNPullModal';
import LeadConversionTiming from '../leadConversionTiming/LeadConversionTiming';
import EmployeeInformation from '../employeeInformation/EmployeeInformation';

// Firebase project configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_ID,
  appId: process.env.REACT_APP_APP_ID,
};

const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
const auth = getAuth(app);
export function ReportQuery({ managerList, adminList, loggedInUser }) {
  const datePickerRef = useRef(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  // SSN Modal Values
  const [ShowSSNModal, setShowSSNModal] = useState(false);

  // Need this also to display the date range in the date picker
  // It's a list b/c you can do several date ranges (not applicable to our case)
  const [calendarDates, setCalendarDates] = useState(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1); // Set to Monday of current week
    const friday = new Date(today);
    friday.setDate(today.getDate() - today.getDay() + 5); // Set to Friday of current week

    return [{
      startDate: monday,
      endDate: friday,
      key: 'selection'
    }];
  });

  const [reportName, setReportName] = useState('');
  const [reportComp, setReportComp] = useState(null);
  const [, setLoggedInUser] = useState({});
  let [loggedIn, setLoggedIn] = useState(true);

  // Log User Out
  const userLogOut = () => {
    signOut(auth)
      .then(() => {
        navigate("/")
      })
      .catch((error) => {
        alert("Unable to sign out: " + error.message);
      })
  }

  // Add this useEffect
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const updateReport = (e) => {
    // console.log("e.target.value: " + e.target.value)
    setReportName(e.target.value);
  };

  // Show report component with Data
  const displayReport = (e) => {
    // console.log("reportName: ", reportName);
    let rc = null;
    if (calendarDates[0].startDate.toLocaleDateString() !== '' && calendarDates[0].endDate.toLocaleDateString() !== '') {
      // console.log("Fetching report for: " + reportName + " from " + calendarDates[0].startDate.toLocaleDateString() + " to " + calendarDates[0].endDate.toLocaleDateString());
      if (reportName === 'callAnalysisBreakdown') {
        rc = <CallAnalysisBreakdown startDate={calendarDates[0].startDate.toLocaleDateString()} endDate={calendarDates[0].endDate.toLocaleDateString()} reportName={reportName} />;
      } else if (reportName === 'callAnalysisBreakdownWithUnderlyingData') {
        rc = <CallAnalysisBreakdownWithUnderlyingData startDate={calendarDates[0].startDate.toLocaleDateString()} endDate={calendarDates[0].endDate.toLocaleDateString()} reportName={reportName} />;
      } else if (reportName === 'lostMoneyBreakdown') {
        rc = <LostMoneyBreakdown startDate={calendarDates[0].startDate.toLocaleDateString()} endDate={calendarDates[0].endDate.toLocaleDateString()} reportName={reportName} />;
      } else if (reportName === 'marketingPerformance') {
        rc = <MarketingPerformance startDate={calendarDates[0].startDate.toLocaleDateString()} endDate={calendarDates[0].endDate.toLocaleDateString()} reportName={reportName} />;
      } else if (reportName === 'leadConversionTiming') {
        rc = <LeadConversionTiming startDate={calendarDates[0].startDate.toLocaleDateString()} endDate={calendarDates[0].endDate.toLocaleDateString()} reportName={reportName} />;
      } else if (reportName === 'salesProductivity') {
        rc = <SalesProductivity startDate={calendarDates[0].startDate.toLocaleDateString()} endDate={calendarDates[0].endDate.toLocaleDateString()} reportName={reportName} />;
      } else if (reportName === 'employeeInformation') {
        rc = <EmployeeInformation startDate={calendarDates[0].startDate.toLocaleDateString()} endDate={calendarDates[0].endDate.toLocaleDateString()} reportName={reportName} />;
      }
    }
    setReportComp(rc);
  };

  const managerReports = {};

  const adminReports = {
    callAnalysisBreakdown: 'Call Analysis Breakdown',
    // CallAnalysisBreakdownWithUnderlyingData: 'Call Analysis Breakdown (with underlying data)', // This one is matching case ANI to call ANI and displays Case Number, call start time and sale date when you expand the rows.
    lostMoneyBreakdown: 'Lost Money Breakdown',
    marketingPerformance: 'Marketing Performance',
    leadConversionTiming: 'Lead Conversion Timing',
    salesProductivity: 'Sales Productivity',
    employeeInformation: 'Employee Information',
  };

  // If the user is a manager, they will get the manager report, if they are admin we combine both lists here
  const reportOptionsMap = managerList && loggedInUser && loggedInUser.email && managerList.includes(loggedInUser.email.toLowerCase())
    ? managerReports
    : adminList && loggedInUser && loggedInUser.email && adminList.includes(loggedInUser.email.toLowerCase())
      ? { ...managerReports, ...adminReports }
      : {};


  // Sort the reports
  const sortedOptions = Object.entries(reportOptionsMap).sort()
  const optionElements = sortedOptions.map(([key, value]) => (
    <option key={key} value={key}>
      {value}
    </option>
  ));

  // Check logged in state
  onAuthStateChanged(auth, (user) => {
    // console.log("user: ", user);
    // Set the user to the state
    setLoggedInUser(user);
    if (user && user.emailVerified) {
      // User is signed in, see docs for a list of available properties
      // https://firebase.google.com/docs/reference/js/auth.user
      // console.log("user.email: ", user.email);
    } else {
      console.log("NOT LOGGED IN/VERIFIED");
      setLoggedIn(false);
    }
  });

  const navigate = useNavigate();
  if (!loggedIn) {
    return navigate("/");
  }

  // Add this function to format the date range display
  const formatDateRange = () => {
    return `${calendarDates[0].startDate.toLocaleDateString()} - ${calendarDates[0].endDate.toLocaleDateString()}`;
  };

  // console.log("optionElements: ", optionElements);

  // Show/Hide SSN Report Modal
  const toggleShowSSNModal = () => {
    setShowSSNModal(!ShowSSNModal);
  };

  return (
    <div className=''>
      <div className='topBar'>
        <div className='f justify-center align-center'>
          <div className='f center-text'>
            <img src={otgLogo} alt="Logo" height={50} className='m-right-2' />
          </div>
          <div className='f gap-5'>
            {/* Logout button */}
            <button className='m-right-2 redButton' onClick={() => userLogOut()}>Logout</button>
            <label className='align-content-center'>Report Name: </label>
            <select
              name="reportName"
              className='large-font rounded-5 dropdown-input'
              onChange={(e) => updateReport(e)}
            >
              <option value={""}></option>
              {optionElements}
            </select>
          </div>
          {/* Date Picker */}
          <div className='date-picker-container m-left-2' ref={datePickerRef}>
            <label className='large-font'>Date Range: </label>
            <button
              className='large-font rounded-5 dropdown-input'
              onClick={() => setShowDatePicker(!showDatePicker)}>
              {formatDateRange()}
            </button>

            {showDatePicker && (
              <div>
                <DateRangePicker
                  className='date-picker-wrapper'
                  onChange={item => {
                    setCalendarDates([item.selection]);
                  }}
                  showSelectionPreview={true}
                  // moveRangeOnFirstSelection={false}
                  // retainEndDateOnFirstSelection={true}
                  months={1}
                  ranges={calendarDates}
                  direction="vertical"
                  editableDateInputs={true}
                  dateDisplayFormat="MM/dd/yyyy"
                />
                {/* <button className='blueButton' onClick={() => setShowDatePicker(false)}>Close</button> */}
              </div>
            )}
          </div>
          <div className='f center-text m-left-2'>
            <button className='blueButton' onClick={(e) => displayReport(e)}>Fetch Report</button>
            <button className='blueButton m-left-2' onClick={() => toggleShowSSNModal() }>SSN Report</button>
          </div>
        </div>
      </div>
      {ShowSSNModal && (
        <SSNPullModal
          toggleShowSSNModal={toggleShowSSNModal}
          adminList={adminList}
        />
      )}
      <div id="reportBox">{reportComp}</div>
    </div>
  );
};
export default ReportQuery;
